import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { matches, useDismiss } from "../hooks";

/** Where a dialog becomes a sheet. Shared with styles.css (search for 640px). */
const SHEET_QUERY = "(max-width: 640px)";

/** Longer than the sheet spring in styles.css, so a lost transitionend
    (a backgrounded tab, a panel with no transition) still closes. */
const CLOSE_FALLBACK_MS = 600;

/** A drag that would come to rest past this share of the sheet dismisses it. */
const DISMISS_FRACTION = 0.5;

/** Moves older than this at release say nothing about the throw: a finger
    that dragged fast and then held still is releasing a stopped sheet. */
const VELOCITY_WINDOW_MS = 100;

let locks = 0;

/**
 * The surface every console dialog is drawn on: a centred panel on a desk, a
 * sheet from the bottom edge on a phone, where the thumb already is.
 *
 * It owns what each dialog used to do for itself (the portal, the scrim,
 * Escape and an outside press, focus in and back) and adds what a phone needs:
 * the page behind stops scrolling, and the sheet can be pulled down to close.
 *
 * Closing is a request, `close`, handed to the body. It plays the exit, along
 * the same path the panel came in on, and only then tells the owner. A dialog
 * the owner closes on its own (a write that succeeded) just unmounts: the
 * page it lands on already says what happened.
 *
 * Portaled to <body>. A `position: fixed` backdrop is only fixed to the
 * viewport while no ancestor has a filter, and `.page-head` has a
 * backdrop-filter: opened from the header, the backdrop covered only the
 * header and the dialog ran off the top of the screen.
 */
export function Modal({
  labelledBy,
  className,
  initialFocus,
  onClose,
  children,
}: {
  labelledBy: string;
  className?: string;
  /** Focused on open. Without one the panel itself takes focus, which keeps
      Escape working without raising a phone's keyboard. */
  initialFocus?: RefObject<HTMLElement | null> | undefined;
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const [state, setState] = useState<"entering" | "open" | "closing">("entering");
  const closing = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    // Nothing to watch where nothing animates (jsdom has no matchMedia, and
    // the tests expect Cancel to close on the spot).
    if (typeof window.matchMedia !== "function") {
      onCloseRef.current();
      return;
    }
    setState("closing");
  }, []);

  const panelRef = useDismiss(true, close);

  // Mount closed, open on the next frame: the transition needs a first style
  // to run from.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setState("open"));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (state !== "closing") return;
    const panel = panelRef.current;
    const done = () => onCloseRef.current();
    const timer = setTimeout(done, CLOSE_FALLBACK_MS);
    function onEnd(event: TransitionEvent) {
      // Transform on the slide or the scale, opacity under reduced motion.
      if (event.target === panel && (event.propertyName === "transform" || event.propertyName === "opacity")) {
        clearTimeout(timer);
        done();
      }
    }
    panel?.addEventListener("transitionend", onEnd);
    return () => {
      clearTimeout(timer);
      panel?.removeEventListener("transitionend", onEnd);
    };
  }, [state, panelRef]);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    (initialFocus?.current ?? panelRef.current)?.focus({ preventScroll: true });
    return () => returnTo?.focus?.({ preventScroll: true });
    // Once, on open: focus is placed, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The page behind holds still. A counter, so a picker opened from inside
  // another dialog does not unlock the page when it closes.
  useEffect(() => {
    const root = document.documentElement;
    if (locks++ === 0) root.classList.add("scroll-locked");
    return () => {
      if (--locks === 0) root.classList.remove("scroll-locked");
    };
  }, []);

  const drag = useSheetDrag(panelRef, close);

  return createPortal(
    <div className="dialog-backdrop" data-state={state}>
      <div
        className={className ? `dialog ${className}` : "dialog"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={panelRef}
        data-state={state}
      >
        <div className="sheet-grabber" aria-hidden="true" {...drag} />
        {children(close)}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Pull-to-dismiss for the sheet, from its grabber strip.
 *
 * The sheet follows the finger 1:1 from wherever it was grabbed, resists being
 * pulled up past its resting place, and on release is thrown to where the
 * gesture was heading rather than where the finger stopped: a short fast
 * flick closes it, a long slow drag that changes its mind does not. Only the
 * strip starts a drag, so the token list inside keeps its own scroll.
 */
function useSheetDrag(panelRef: RefObject<HTMLDivElement | null>, close: () => void) {
  const start = useRef<{ y: number; id: number } | null>(null);
  const trail = useRef<{ y: number; t: number }[]>([]);

  function place(offset: number | null) {
    const panel = panelRef.current;
    if (!panel) return;
    if (offset === null) {
      panel.style.removeProperty("transform");
      panel.style.removeProperty("transition");
    } else {
      panel.style.transition = "none";
      panel.style.transform = `translateY(${offset}px)`;
    }
  }

  return {
    onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
      if (!matches(SHEET_QUERY)) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      start.current = { y: event.clientY, id: event.pointerId };
      trail.current = [{ y: event.clientY, t: event.timeStamp }];
    },
    onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
      const from = start.current;
      if (!from || from.id !== event.pointerId) return;
      const dy = event.clientY - from.y;
      trail.current = [...trail.current.slice(-4), { y: event.clientY, t: event.timeStamp }];
      place(dy >= 0 ? dy : rubberband(dy, panelRef.current?.offsetHeight ?? 400));
    },
    onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
      const from = start.current;
      if (!from || from.id !== event.pointerId) return;
      start.current = null;
      const dy = event.clientY - from.y;
      const height = panelRef.current?.offsetHeight ?? 400;
      const released = { y: event.clientY, t: event.timeStamp };
      const recent = [...trail.current, released].filter(
        (point) => released.t - point.t <= VELOCITY_WINDOW_MS,
      );
      const resting = dy + project(velocity(recent));
      // The inline offset is cleared in the same frame the state flips, so
      // the transition runs from where the finger left the sheet.
      place(null);
      if (dy > 0 && resting > height * DISMISS_FRACTION) close();
    },
    onPointerCancel() {
      start.current = null;
      place(null);
    },
  };
}

/** px/s across the given moves; zero without two of them to compare. */
function velocity(trail: { y: number; t: number }[]): number {
  const first = trail[0];
  const last = trail[trail.length - 1];
  if (!first || !last) return 0;
  const dt = last.t - first.t;
  return dt > 0 ? ((last.y - first.y) / dt) * 1000 : 0;
}

/** Where a released gesture comes to rest, as scroll deceleration projects it
    (Apple, Designing Fluid Interfaces). */
function project(velocityPxPerSecond: number, decelerationRate = 0.998): number {
  return ((velocityPxPerSecond / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** Resistance that grows with the overshoot, so an edge gives rather than stops. */
function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}
